import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, UserMinus } from 'lucide-react';
import { format } from 'date-fns';

const reasonLabels: Record<string, string> = {
  left_shop: 'Left voluntarily',
  removed_by_shop: 'Removed by shop',
  account_deactivated: 'Account deactivated',
  shop_canceled: 'Shop closed',
};

// Batch 15: a Former Members entry comes from one of two canonical
// sources, kept disjoint to preserve history and avoid duplication:
//
//   - 'membership'   → row in `memberships` with status='removed'
//                      (auth-user-bound roles: MANAGER / SERVICE_WRITER
//                      / OWNER / TECHNICIAN-who-accepted-an-invite,
//                      removed via Team Management).
//
//   - 'technician'   → row in `technicians` with
//                      employment_status='Inactive' (the soft-archive
//                      state written by ShopProfile.handleDeleteTechnician,
//                      Batch 9F decision). Most shop technicians are
//                      tech-only rows with no memberships row, so the
//                      pre-Batch-15 query missed them entirely.
//
// `removedAt` is the canonical removal timestamp: memberships.removed_at
// for membership rows, technicians.updated_at as a proxy for archive
// rows (the row's last write — which, after archiving, was the
// employment_status flip). We sort the merged list by removedAt desc so
// the most-recent removals surface at the top regardless of source.
type FormerEntry = {
  key: string;
  source: 'membership' | 'technician';
  name: string;
  email: string | null;
  role: string | null;
  removedAt: string | null;
  removedBy: string | null;
  reason: string | null;
};

/**
 * Former Members tab for Shop Profile.
 * Shows removed memberships (auth-bound roles) AND archived technicians
 * (employment_status='Inactive') in a single timeline.
 */
export function FormerMembersTab() {
  const { activeShop } = useAuth();

  const { data: formerMembers, isLoading } = useQuery({
    queryKey: ['former-members', activeShop?.id],
    queryFn: async (): Promise<FormerEntry[]> => {
      if (!activeShop?.id) return [];

      // Fetch in parallel — independent reads.
      const [membershipsRes, techsRes] = await Promise.all([
        supabase
          .from('memberships')
          .select('id, user_id, role, removed_at, removed_by, remove_reason')
          .eq('shop_id', activeShop.id)
          .eq('status', 'removed')
          .order('removed_at', { ascending: false }),
        // Archived technicians via the established soft-delete pattern.
        // `updated_at` proxies the archive timestamp because Batch 9F
        // archived via `update({ employment_status: 'Inactive' })` and
        // never introduced a dedicated archived_at column. If a future
        // batch adds one, swap it in here without changing the read
        // shape.
        supabase
          .from('technicians')
          .select('id, name, role, employment_status, updated_at')
          .eq('shop_id', activeShop.id)
          .eq('employment_status', 'Inactive')
          .order('updated_at', { ascending: false }),
      ]);

      if (membershipsRes.error) throw membershipsRes.error;
      if (techsRes.error) throw techsRes.error;

      const memberships = membershipsRes.data ?? [];
      const techs = techsRes.data ?? [];

      // Profiles lookup for membership rows (display name + email +
      // removed-by attribution). Skip the call entirely when there are
      // no memberships to avoid a 1-row-wasted PostgREST roundtrip.
      let profileMap = new Map<string, { id: string; email: string | null; display_name: string | null }>();
      if (memberships.length > 0) {
        const profileIds = new Set<string>();
        for (const m of memberships) {
          if (m.user_id) profileIds.add(m.user_id);
          if (m.removed_by) profileIds.add(m.removed_by);
        }
        if (profileIds.size > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, email, display_name')
            .in('id', [...profileIds]);
          profileMap = new Map((profiles ?? []).map((p: any) => [p.id, p]));
        }
      }

      const membershipEntries: FormerEntry[] = memberships.map((m) => {
        const profile = profileMap.get(m.user_id);
        const removedByProfile = m.removed_by ? profileMap.get(m.removed_by) : null;
        return {
          key: `m:${m.id}`,
          source: 'membership' as const,
          name: profile?.display_name || 'Unknown',
          email: profile?.email ?? null,
          role: m.role ?? null,
          removedAt: m.removed_at ?? null,
          removedBy: removedByProfile?.display_name || (m.removed_by ? 'System' : null),
          reason: m.remove_reason ?? null,
        };
      });

      const techEntries: FormerEntry[] = techs.map((t) => ({
        key: `t:${t.id}`,
        source: 'technician' as const,
        name: t.name || 'Unknown',
        email: null,
        role: t.role ?? null,
        removedAt: t.updated_at ?? null,
        removedBy: null,
        reason: 'removed_by_shop',
      }));

      // Merge + sort desc by removedAt (nulls last).
      const all = [...membershipEntries, ...techEntries];
      all.sort((a, b) => {
        const aT = a.removedAt ? Date.parse(a.removedAt) : 0;
        const bT = b.removedAt ? Date.parse(b.removedAt) : 0;
        return bT - aT;
      });
      return all;
    },
    enabled: !!activeShop?.id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading former members...
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserMinus className="h-5 w-5" />
          Former Members
        </CardTitle>
        <CardDescription>
          Members and technicians who have been removed from this shop. Historical records, tickets, and time logs remain intact.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!formerMembers || formerMembers.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No former members</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Last Role</TableHead>
                <TableHead>Removed</TableHead>
                <TableHead>By</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {formerMembers.map((m) => (
                <TableRow key={m.key}>
                  <TableCell className="font-medium">{m.name}</TableCell>
                  <TableCell className="text-muted-foreground">{m.email ?? '—'}</TableCell>
                  <TableCell>
                    {m.role ? <Badge variant="secondary">{m.role}</Badge> : '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {m.removedAt ? format(new Date(m.removedAt), 'MMM d, yyyy') : '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{m.removedBy ?? '—'}</TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {reasonLabels[m.reason || ''] || m.reason || '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {m.source === 'technician' ? 'Archived' : 'Former'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
